import type { LifecycleEvent, LifecycleObserver } from './chain';
import type { BrandConfig, AuditRecord } from '../branding/types';
import { AuditLogStore } from '../storage/audit-log-store';
import { getLogger } from '../logging/logger';

interface InFlight {
  jobId: string;
  attendanceId: string;
  attempt: number;
  poolId: string;
  poolName?: string;
  executor: string;
  prompt: string;
  responseRaw: string;
  responseParsed?: Record<string, unknown>;
  outputs: Array<{ label: string; content: Record<string, unknown> }>;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  model?: string;
  claimedAt: string;
}

/**
 * Built-in observer that writes one append-only JSONL record per closed
 * attendance. Default path: `${configDir}/audit.jsonl`; the brand may
 * override via `brand.audit.path`. Per-record fsync; see storage/audit-log-store.ts.
 *
 * The observer assembles state across the dispatch's lifecycle, then
 * flushes a single record on `attendance.closed`. A write failure here is
 * logged and dropped; the substrate's chain isolates non-veto events from
 * the job lifecycle.
 *
 * Sofia's LifecycleEvent shape (from `@whittlelabs/jobs`):
 *  - `job.claimed.dispatch`      carries the full DispatchedJob (pool, job, inputs)
 *  - `executor.dispatch.started.rendered.prompt` carries the rendered prompt
 *  - `executor.dispatch.completed.response`      carries rawText, parsed, usage
 *  - `output.submitted`          carries label + content
 *  - `attendance.closed`         carries status, reason, totalDurationMs
 */
export function createAuditLogObserver(
  brand: BrandConfig,
  auditFilePath: string,
): LifecycleObserver {
  const store = new AuditLogStore(auditFilePath);
  const inFlight = new Map<string, InFlight>();
  const recordHook = brand.audit?.recordHook;

  return {
    name: 'audit-log',
    async notify(event: LifecycleEvent): Promise<void> {
      switch (event.kind) {
        case 'job.claimed': {
          const dispatch = event.dispatch;
          inFlight.set(event.jobId, {
            jobId: event.jobId,
            attendanceId: event.attendanceId,
            attempt: event.attempt,
            poolId: dispatch.pool.id,
            poolName: dispatch.pool.name,
            executor: '',
            prompt: '',
            responseRaw: '',
            outputs: [],
            claimedAt: event.claimedAt,
          });
          return;
        }

        case 'executor.dispatch.started': {
          const entry = inFlight.get(event.jobId);
          if (entry) {
            entry.executor = event.executor;
            // The prompt is rendered inside the executor now (ADR 0005); the
            // audit record captures the strategy + the response, not the prompt.
            entry.prompt = `strategy:${event.strategy}`;
          }
          return;
        }

        case 'executor.dispatch.completed': {
          const entry = inFlight.get(event.jobId);
          if (entry) {
            entry.executor = event.executor;
            entry.responseRaw = event.response.rawText;
            entry.responseParsed = event.response.parsed;
            entry.inputTokens = event.response.usage?.inputTokens;
            entry.outputTokens = event.response.usage?.outputTokens;
            entry.costUsd = event.response.usage?.costUsd;
            entry.model = event.response.usage?.aiModel;
          }
          return;
        }

        case 'output.submitted': {
          const entry = inFlight.get(event.jobId);
          if (entry) {
            entry.outputs.push({ label: event.label, content: event.content });
          }
          return;
        }

        case 'attendance.closed': {
          const entry = inFlight.get(event.jobId);
          inFlight.delete(event.jobId);
          if (!entry) {
            // We never saw the dispatch; emit a thin record so the line
            // exists in the log.
            const skeleton: AuditRecord = {
              schemaVersion: 1,
              jobId: event.jobId,
              attendanceId: event.attendanceId,
              attempt: 0,
              poolId: '',
              executor: 'unknown',
              prompt: '',
              responseRaw: '',
              outputs: [],
              status: event.status,
              reason: event.reason,
              claimedAt: new Date().toISOString(),
              completedAt: new Date().toISOString(),
              durationMs: event.totalDurationMs,
            };
            await writeSafely(store, skeleton, recordHook);
            return;
          }

          const record: AuditRecord = {
            schemaVersion: 1,
            jobId: entry.jobId,
            attendanceId: entry.attendanceId,
            attempt: entry.attempt,
            poolId: entry.poolId,
            poolName: entry.poolName,
            executor: entry.executor || 'unknown',
            model: entry.model,
            inputTokens: entry.inputTokens,
            outputTokens: entry.outputTokens,
            costUsd: entry.costUsd,
            prompt: entry.prompt,
            responseRaw: entry.responseRaw,
            responseParsed: entry.responseParsed,
            outputs: entry.outputs,
            status: event.status,
            reason: event.reason,
            claimedAt: entry.claimedAt,
            completedAt: new Date().toISOString(),
            durationMs: event.totalDurationMs,
          };

          await writeSafely(store, record, recordHook);
          return;
        }

        default:
          return;
      }
    },
  };
}

async function writeSafely(
  store: AuditLogStore,
  record: AuditRecord,
  hook: ((r: AuditRecord) => AuditRecord | null) | undefined,
): Promise<void> {
  const final = hook ? hook(record) : record;
  if (final === null) return;
  try {
    await store.append(final);
  } catch (err) {
    getLogger().error('Audit log write failed', {
      jobId: record.jobId,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
